import React, { useState } from "react";
import { useAccount, useSignMessage } from "wagmi";
import { useAppKit } from "@reown/appkit/react";
import { unlinkWallet } from "@/services/auth.service";
import { linkConnectedWallet, shortAddress } from "@/lib/wallet";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Wallet as WalletIcon } from "lucide-react";
import { Hint } from "./SettingsShared";

const WalletConnect = ({ user, refreshUser }) => {
  const [busy, setBusy] = useState(false);
  const { address, chainId, isConnected } = useAccount();
  const { signMessageAsync } = useSignMessage();
  const { open } = useAppKit();
  const linked = user.wallet_address;

  const link = async () => {
    setBusy(true);
    try {
      await linkConnectedWallet({ address, chainId, signMessageAsync });
      toast.success("Wallet linked");
      await refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.shortMessage || e.message || "Failed to link wallet");
    } finally {
      setBusy(false);
    }
  };

  const unlink = async () => {
    setBusy(true);
    try {
      await unlinkWallet();
      toast.success("Wallet unlinked");
      await refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (linked) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3 p-4 border border-border rounded-lg" data-testid="linked-wallet-row">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Linked wallet</div>
            <div className="mt-1 font-mono break-all">{user.wallet_address}</div>
          </div>
          <WalletIcon className="w-5 h-5 shrink-0 text-primary" />
        </div>
        <Button variant="outline" onClick={unlink} disabled={busy} data-testid="unlink-wallet-btn">
          {busy ? "Working…" : "Unlink wallet"}
        </Button>
        <Hint>Unlink to connect a different wallet. Funding and claiming use the linked address.</Hint>
      </div>
    );
  }

  if (isConnected) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3 p-4 border border-border rounded-lg">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">Connected</div>
            <div className="mt-1 font-mono">{shortAddress(address)}</div>
          </div>
          <button type="button" onClick={() => open()} className="text-xs font-semibold text-primary hover:underline" data-testid="switch-wallet-btn">
            Switch
          </button>
        </div>
        <Button onClick={link} disabled={busy} data-testid="link-wallet-btn">
          <WalletIcon className="w-4 h-4 mr-1.5" /> {busy ? "Check your wallet…" : "Link this wallet"}
        </Button>
        <Hint>You'll sign a message to prove ownership (SIWE). No transaction or gas is needed to link.</Hint>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="p-8 text-sm text-center border border-dashed rounded-lg border-border text-muted-foreground" data-testid="wallet-empty">
        No wallet linked. Connect one to fund escrow and claim payments on-chain.
      </div>
      <Button onClick={() => open()} data-testid="connect-wallet-btn">
        <WalletIcon className="w-4 h-4 mr-1.5" /> Connect wallet
      </Button>
    </div>
  );
};

export default WalletConnect;
